import { map } from 'es-toolkit/compat';
import { useState } from 'react';

import { SpotDetectionCountBin } from 'interfaces/calculateFeatures';
import { normalizeSpotDetectionCountBins } from '../../../spotDetectionInputSetup';

export interface HScoreBinRow {
  score: number;
  minimum: number | null;
  maximum: number | null;
  maximumDraft: string | null;
}

interface UseHScoreBinsParams {
  countBins: SpotDetectionCountBin[];
  onChange: (countBins: SpotDetectionCountBin[]) => void;
  controlledMaximumDrafts?: string[];
  onMaximumDraftsChange?: (drafts: string[]) => void;
}

export const getHScoreBinMaximumDrafts = (countBins: SpotDetectionCountBin[]): string[] => {
  const normalizedBins = normalizeSpotDetectionCountBins(countBins);
  return [
    String((normalizedBins[0]?.minCount ?? 1) - 1),
    ...map(normalizedBins.slice(0, -1), (countBin) => String(countBin.maxCount)),
  ];
};

export const getHScoreBinDraftValidation = (maximumDrafts: string[]): { error?: string; invalidScore?: number } => {
  if (maximumDrafts.length === 0) return { error: 'Add at least one positive score bin.', invalidScore: 0 };
  const maximums = map(maximumDrafts, Number);
  const nonIntegerScore = maximumDrafts.findIndex(
    (maximum, score) => maximum.trim() === '' || !Number.isInteger(maximums[score])
  );

  if (nonIntegerScore !== -1) {
    return { error: 'Maximums must be whole numbers.', invalidScore: nonIntegerScore };
  }
  if (maximums[0] < 0) {
    return { error: 'Score 0 maximum must be at least 0.', invalidScore: 0 };
  }
  const nonIncreasingScore = maximums.findIndex((maximum, score) => score > 0 && maximum <= maximums[score - 1]);
  if (nonIncreasingScore !== -1) {
    return {
      error: 'Each maximum must be greater than the previous maximum.',
      invalidScore: nonIncreasingScore,
    };
  }
  return {};
};

export const getHScoreCountBinsFromMaximums = (maximumDrafts: string[]): SpotDetectionCountBin[] => {
  const maximums = map(maximumDrafts, Number);

  return map(maximums, (maximum, index) => ({
    minCount: maximum + 1,
    maxCount: index < maximums.length - 1 ? maximums[index + 1] : undefined,
  }));
};

const getRows = (maximumDrafts: string[]): HScoreBinRow[] => {
  const parsedMaximums = map(maximumDrafts, (maximum) => {
    const parsedMaximum = Number(maximum);
    return maximum.trim() !== '' && Number.isInteger(parsedMaximum) ? parsedMaximum : null;
  });

  return map([...maximumDrafts, null], (maximumDraft, score) => {
    const previousMaximum = parsedMaximums[score - 1];

    return {
      score,
      minimum: score === 0 ? 0 : previousMaximum === null ? null : previousMaximum + 1,
      maximum: score === maximumDrafts.length ? null : parsedMaximums[score],
      maximumDraft,
    };
  });
};

export const useHScoreBins = ({
  countBins,
  onChange,
  controlledMaximumDrafts,
  onMaximumDraftsChange,
}: UseHScoreBinsParams) => {
  const externalMaximumDrafts = controlledMaximumDrafts ?? getHScoreBinMaximumDrafts(countBins);
  const externalKey = externalMaximumDrafts.join(',');
  const [localMaximumDrafts, setMaximumDrafts] = useState(externalMaximumDrafts);
  const maximumDrafts = controlledMaximumDrafts ?? localMaximumDrafts;
  const [previousExternalKey, setPreviousExternalKey] = useState(externalKey);

  if (externalKey !== previousExternalKey) {
    setPreviousExternalKey(externalKey);
    setMaximumDrafts(externalMaximumDrafts);
  }

  const { error, invalidScore } = getHScoreBinDraftValidation(maximumDrafts);

  const updateAndEmit = (nextMaximumDrafts: string[]) => {
    if (onMaximumDraftsChange) {
      onMaximumDraftsChange(nextMaximumDrafts);
      return;
    }
    setMaximumDrafts(nextMaximumDrafts);
    if (!getHScoreBinDraftValidation(nextMaximumDrafts).error) {
      onChange(getHScoreCountBinsFromMaximums(nextMaximumDrafts));
    }
  };

  const updateMaximum = (score: number, value: string) => {
    if (score < 0 || score >= maximumDrafts.length) {
      return;
    }
    const nextMaximumDrafts = [...maximumDrafts];
    nextMaximumDrafts[score] = value;
    updateAndEmit(nextMaximumDrafts);
  };

  const addScore = () => {
    if (error) {
      return;
    }
    const finalScoreMinimum = Number(maximumDrafts.at(-1)) + 1;
    updateAndEmit([...maximumDrafts, String(finalScoreMinimum)]);
  };

  const updateFirstMinimum = (value: string) => {
    if (value.trim() === '' || !Number.isInteger(Number(value)) || Number(value) < 1) {
      updateAndEmit([value.trim() === '' ? '' : String(Number(value) - 1), ...maximumDrafts.slice(1)]);
      return;
    }
    const currentMinimum = getHScoreBinDraftValidation(maximumDrafts).error
      ? countBins[0]?.minCount ?? 1
      : Number(maximumDrafts[0]) + 1;
    const difference = Number(value) - currentMinimum;
    updateAndEmit(
      map(maximumDrafts, (maximum, index) =>
        index === 0
          ? String(Number(value) - 1)
          : maximum.trim() !== '' && Number.isFinite(Number(maximum))
          ? String(Number(maximum) + difference)
          : maximum
      )
    );
  };

  const removeScore = (score: number) => {
    if (maximumDrafts.length === 1 || score < 1 || score > maximumDrafts.length) {
      return;
    }
    updateAndEmit(maximumDrafts.filter((_maximum, index) => index !== score - 1));
  };

  return {
    rows: getRows(maximumDrafts),
    error,
    invalidScore,
    canAddScore: !error,
    canRemoveScore: maximumDrafts.length > 1,
    updateMaximum,
    updateFirstMinimum,
    addScore,
    removeScore,
  };
};
